import { z } from "zod";

import {
  PrototypeExpr,
  PrototypeItemValue,
  PrototypeStateValue,
} from "./expr.ts";

export {
  PrototypeItemValue,
  PrototypeStateEntry,
  PrototypeStateSlice,
  PrototypeStateValue,
} from "./expr.ts";
export type { PrototypeItemContext } from "./expr.ts";

/** The fixed runtime library; the LLM only generates calls to these. */
export const PrototypeActionFn = z.enum([
  "increment",
  "decrement",
  "toggle",
  "set",
  "append",
  "unshift",
  "pop",
  "shift",
  "remove",
  "clear",
  "setItem",
  "toggleItem",
  "if",
]);
export type PrototypeActionFn = z.infer<typeof PrototypeActionFn>;

const PrototypeActionRecordValue = z.union([
  PrototypeStateValue,
  z.object({ event: z.literal(true) }),
  z.object({ ref: z.string() }),
  z.object({ expr: PrototypeExpr }),
]);

// {event}=handler payload `v`, {ref}=state key, {item}/{index}=list scope,
// {record}/{list}=object/array literals, {expr}=any computed value
export const PrototypeActionArg = z.union([
  PrototypeStateValue,
  z.object({ event: z.literal(true) }),
  z.object({ ref: z.string() }),
  z.object({ item: z.union([z.literal(true), z.string()]) }),
  z.object({ index: z.literal(true) }),
  z.object({ record: z.record(z.string(), PrototypeActionRecordValue) }),
  z.object({ list: z.array(PrototypeItemValue) }),
  z.object({ expr: PrototypeExpr }),
]);
export type PrototypeActionArg = z.infer<typeof PrototypeActionArg>;

// a serialized runtime call; `fn: "if"` is the branch statement — `cond`
// selects the `then`/`else` statement list
export type PrototypeAction = {
  fn: PrototypeActionFn;
  args?: PrototypeActionArg[];
  cond?: PrototypeExpr;
  then?: PrototypeAction[];
  else?: PrototypeAction[];
};
export const PrototypeAction: z.ZodType<PrototypeAction> = z.lazy(() =>
  z
    .object({
      fn: PrototypeActionFn,
      args: z.array(PrototypeActionArg).optional(),
      cond: PrototypeExpr.optional(),
      then: z.array(PrototypeAction).optional(),
      else: z.array(PrototypeAction).optional(),
    })
    .refine(
      (action) =>
        action.fn === "if"
          ? action.cond !== undefined && action.then !== undefined
          : action.args !== undefined,
      { message: "`if` needs cond+then; every other fn needs args" },
    ),
);

/** A single action, or an ordered statement list (block-body handler). */
export const PrototypeHandler = z.union([
  PrototypeAction,
  z.array(PrototypeAction),
]);
export type PrototypeHandler = z.infer<typeof PrototypeHandler>;

/**
 * A computed value wired to a render target: "visible" | "className"
 * (appended) | "style:<prop>" (`unit` suffixes bare numbers) | "prop:<name>".
 */
export const PrototypeBinding = z.object({
  target: z.string(),
  expr: PrototypeExpr,
  unit: z.string().optional(),
});
export type PrototypeBinding = z.infer<typeof PrototypeBinding>;

export type BindingTarget =
  | { kind: "visible" }
  | { kind: "className" }
  | { kind: "prop"; name: string }
  | { kind: "style"; name: string }
  | { kind: "unknown" };

/** The one decoder for binding target strings; consumers ignore "unknown". */
export const parseBindingTarget = (target: string): BindingTarget => {
  if (target === "visible") return { kind: "visible" };
  if (target === "className") return { kind: "className" };
  if (target.startsWith("prop:")) {
    return { kind: "prop", name: target.slice("prop:".length) };
  }
  if (target.startsWith("style:")) {
    return { kind: "style", name: target.slice("style:".length) };
  }
  return { kind: "unknown" };
};

// list template (`state.cart.map(…)`), rendered as item 0 on canvas; `display`
// stashes the authored display for restore when an empty array baked it hidden
export const RepeatBinding = z.object({
  key: z.string(),
  display: z.string().optional(),
});
export type RepeatBinding = z.infer<typeof RepeatBinding>;

/** Activated only by the prototype player; the canvas ignores it (like `navigation`). */
export const ElementInteractions = z.object({
  onClick: PrototypeHandler.optional(),
  onValueChange: PrototypeHandler.optional(),
  onCheckedChange: PrototypeHandler.optional(),
  onChange: PrototypeHandler.optional(),
  bindings: z.array(PrototypeBinding).optional(),
  repeat: RepeatBinding.optional(),
  // canvas-only unrolled list sibling (items 1..n-1); the prototype drops it
  repeatClone: z.literal(true).optional(),
  // authored layout display, stashed when initial-state visibility bakes the
  // element hidden for the canvas ("" = no authored display)
  display: z.string().optional(),
  // game-loop hooks, honored on the screen root while mounted
  onTick: z.object({ ms: z.number(), run: PrototypeHandler }).optional(),
  onKey: z.record(z.string(), PrototypeHandler).optional(),
});
export type ElementInteractions = z.infer<typeof ElementInteractions>;

/** Event props the parser whitelists and the player/serializer wire. */
export const PROTOTYPE_EVENT_PROPS = [
  "onClick",
  "onValueChange",
  "onCheckedChange",
  "onChange",
] as const;
export type PrototypeEventProp = (typeof PROTOTYPE_EVENT_PROPS)[number];

/** Reserved state key: "light" | "dark" re-themes the preview ("auto" → light). */
export const PROTOTYPE_APPEARANCE_KEY = "appearance";
export type PrototypeAppearance = "light" | "dark";
export const resolvePrototypeAppearance = (v: unknown): PrototypeAppearance =>
  v === "dark" ? "dark" : "light";
